'use client';

import { ArrowLeft } from 'lucide-react';

import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LandingSilk } from '@/components/landing/LandingSilk';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { useAuthToken } from '@/hooks/useAuthToken';
import { CannySidebar } from './CannySidebar';
import { CannyEmptyState } from './CannyEmptyState';
import { CannyComposer } from './CannyComposer';
import dynamic from 'next/dynamic';

// react-markdown тянет свой парсер. На пустом экране сообщений нет вовсе,
// поэтому грузим его только когда появился первый ответ.
const CannyMarkdown = dynamic(() => import('./CannyMarkdown').then((m) => m.CannyMarkdown), {
  ssr: false,
});
import { CannyAttachments } from './CannyAttachments';
import { CannyAttachmentPreview } from './CannyAttachmentPreview';
import { useCannyDocuments } from './useCannyDocuments';
import { useCannyChat, useCannyVideos } from './useCannyChat';
import { useCannyImages, isImageFile } from './useCannyImages';
import type { MentionItem } from './CannyMentionMenu';
import { CannyToolPart } from './CannyToolPart';

function authHeaders(): Record<string, string> {
  const token = typeof window === 'undefined' ? null : localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Тёмный фон лендинга: тот же WebGL-silk, что на главной, плюс затемнение. */
function CannyBackdrop({ dimmed }: { dimmed: boolean }) {
  return (
    <>
      {/*
        Силк живой в пустом состоянии — это связь с лендингом. Как только пошёл
        диалог, он притухает: движущийся градиент под длинным текстом утомляет
        и роняет контраст. Слой затемнения поверх держит контраст текста.
      */}
      <LandingSilk
        baseColor="#28137F"
        highlightColor="#4144E6"
        className={`pointer-events-none fixed inset-0 z-0 transition-opacity duration-700 ${
          dimmed ? 'opacity-20' : 'opacity-70'
        }`}
      />
      {/* Плотное затемнение: без него силк перебивает текст и роняет контраст. */}
      <div className="fixed inset-0 z-0 bg-[#09031a]/80" />
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-black/30 via-transparent to-black/60" />
    </>
  );
}

export function CannyShell() {
  const { isAuthenticated } = useAuth();
  const { t } = useI18n();
  const [collapsed, setCollapsed] = useState(false);
  const [input, setInput] = useState('');
  const [dragging, setDragging] = useState(false);
  // Референс общий на чат: приложил один раз — используется всеми генерациями.
  const [referenceUrl, setReferenceUrl] = useState<string | null>(null);
  // Картинка, присланная скрепкой, — тоже референс. Без этого клиент
  // прикладывает своё фото, просит «как я захожу в кадр», а генерация уходит
  // в text-to-video и рисует незнакомого человека.
  const { authedFetch } = useAuthToken();
  const [attachedReference, setAttachedReference] = useState<string | null>(null);
  // Пока восстанавливаем чат из адреса, пустой экран показывать нельзя:
  // клиент видел вспышку «Чем займёмся?» поверх своего разговора.
  //
  // Стартовое значение ОДИНАКОВОЕ на сервере и в браузере. Читать здесь
  // window.location нельзя: на сервере его нет, разметка выходила разной,
  // React падал на гидратации (#418) и уносил с собой весь чат — сообщения
  // переставали отправляться.
  const [restoring, setRestoring] = useState(true);

  const {
    chats,
    chatId,
    limitMessage,
    claimAnonymous,
    messages,
    busy,
    hasMessages,
    addMention,
    loadChats,
    createChat,
    openChat,
    removeChat,
    send: sendMessage,
  } = useCannyChat();
  const { documents, upload, remove, uploadError } = useCannyDocuments(chatId);
  const { images, add: addImage, remove: removeImage, clear: clearImages, error: imageError } = useCannyImages();

  // Скрепка одна, а назначения два: картинка относится к одному сообщению
  // («хочу похожий пост»), документ уходит в базу знаний бренда навсегда.
  const attach = useCallback(
    (file: File) => {
      if (isImageFile(file)) void addImage(file);
      else void upload(file);
    },
    [addImage, upload],
  );
  const videos = useCannyVideos(isAuthenticated);

  // Готовые документы — их и показываем миниатюрами над последней репликой.
  const pendingAttachments = documents
    .filter((doc) => doc.status === 'ready')
    .map((doc) => ({ id: doc.id, name: doc.name }));

  // Список для `@`-меню: документы бренда и ролики библиотеки.
  const mentionItems: MentionItem[] = [
    ...documents.map((doc) => ({ id: doc.id, title: doc.name, kind: 'document' as const })),
    ...videos.map((video) => ({ id: video.id, title: video.title, kind: 'video' as const })),
  ];

  useEffect(() => {
    if (!isAuthenticated) return;
    // Сначала забираем демо-разговор, потом грузим список: иначе только что
    // перенесённый чат не появится до перезагрузки страницы.
    void (async () => {
      await claimAnonymous();
      await loadChats();
      // Возвращаем клиента в тот чат, что открыт в адресе. Иначе F5 показывал
      // пустой «Новый чат», и разговор выглядел потерянным, хотя лежал в базе.
      const wanted = new URLSearchParams(window.location.search).get('c');
      if (wanted) await openChat(wanted);
      setRestoring(false);
    })().catch(() => setRestoring(false));
  }, [claimAnonymous, isAuthenticated, loadChats, openChat]);

  /**
   * Запоминает запуск генерации на сервере.
   *
   * Промах не показываем: ролики уже делаются, и ошибка сохранения состояния
   * не то, чем стоит прерывать клиента. Хуже будет только при перезагрузке —
   * карточка вернётся к кнопке, как раньше.
   */
  const rememberGeneration = useCallback(
    async (messageId: string, toolIndex: number, jobs: unknown[]) => {
      if (!chatId) return;
      try {
        await authedFetch(`/api/canny/chats/${chatId}/generation`, {
          method: 'POST',
          body: JSON.stringify({ messageId, toolIndex, jobs }),
        });
      } catch (error) {
        console.error('[canny] не удалось запомнить запуск генерации', error);
      }
    },
    [authedFetch, chatId],
  );

  const send = useCallback(
    async (text: string) => {
      setInput('');
      const urls = images.map((image) => image.url);
      // Последняя приложенная картинка становится референсом для генерации.
      if (urls.length > 0) setAttachedReference(urls[urls.length - 1]);
      // Превью чистим СРАЗУ: sendMessage ждёт весь стрим ответа, и до его
      // конца картинка висела под полем ввода, дублируя ту, что уже улетела.
      clearImages();
      await sendMessage(text, urls);
    },
    [clearImages, images, sendMessage],
  );

  return (
    <main
      className="new-landing-shell relative flex min-h-screen overflow-hidden bg-[#09031a] text-white"
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(event) => {
        // Уходим из подсветки только когда курсор покинул всё полотно,
        // а не при переходе между вложенными элементами.
        if (event.currentTarget.contains(event.relatedTarget as Node)) return;
        setDragging(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        const file = event.dataTransfer.files?.[0];
        if (file && isAuthenticated) attach(file);
      }}
    >
      <CannyBackdrop dimmed={hasMessages} />

      {dragging && (
        <div className="pointer-events-none fixed inset-4 z-30 rounded-3xl border-2 border-dashed border-white/40 bg-black/40" />
      )}

      <div className="relative z-10 flex min-h-screen w-full">
        <CannySidebar
          chats={chats}
          activeChatId={chatId}
          collapsed={collapsed || !isAuthenticated}
          onToggle={() => setCollapsed((value) => !value)}
          onSelect={openChat}
          onCreate={() => void createChat()}
          onDelete={(id) => void removeChat(id)}
        />

        <section className="flex min-w-0 flex-1 flex-col">
          <header className="flex items-center justify-between px-5 py-4">
            {/* Заголовок был ссылкой на главную, но выглядел как подпись —
                клиент не понимал, как выйти из чата. Стрелка читается сразу. */}
            <div className="flex items-center gap-2">
              <Link
                href="/"
                aria-label={t('canny.backHome')}
                title={t('canny.backHome')}
                className="flex h-8 w-8 items-center justify-center rounded-full text-white/45 transition-colors hover:bg-white/10 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
              </Link>
              <span className="text-sm font-medium tracking-tight text-white/85">Canny</span>
            </div>
            {isAuthenticated ? (
              <span className="text-xs uppercase tracking-[0.24em] text-white/30">CANNECT</span>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  href="/login"
                  className="inline-flex h-9 items-center rounded-full bg-white px-4 text-sm font-medium text-[#09031a]"
                >
                  {t('canny.auth.login')}
                </Link>
                <Link
                  href="/login"
                  className="liquid-glass inline-flex h-9 items-center rounded-full px-4 text-sm text-white/85 transition-colors hover:text-white"
                >
                  {t('canny.auth.signup')}
                </Link>
              </div>
            )}
          </header>

          {hasMessages || restoring ? (
            <>
              <div className="flex-1 overflow-y-auto px-4">
                <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 py-6">
                  {messages.map((message, messageIndex) => {
                    // Превью показываем только над последним сообщением клиента:
                    // документы привязаны к бренду, а не к конкретной реплике,
                    // и дублировать их над каждой было бы ложью.
                    const isLastUser =
                      message.role === 'user' &&
                      messageIndex === messages.map((m) => m.role).lastIndexOf('user');
                    const text = message.parts
                      .filter((part): part is { type: 'text'; text: string } => part.type === 'text')
                      .map((part) => part.text)
                      .join('');
                    // Сообщение может состоять из одной карточки без текста —
                    // отбрасывать его нельзя, иначе предложение брони исчезнет.
                    const hasTools = message.parts.some(
                      (part) => typeof part.type === 'string' && part.type.startsWith('tool-'),
                    );
                    // Картинки, приложенные к этой реплике.
                    const files = message.parts.filter(
                      (part): part is { type: 'file'; url: string; mediaType: string } =>
                        part.type === 'file' && typeof (part as { url?: unknown }).url === 'string',
                    );
                    if (!text && !hasTools && files.length === 0) return null;
                    return (
                      <div
                        key={message.id}
                        className={message.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
                      >
                        {message.role === 'user' ? (
                          <div className="flex max-w-[80%] flex-col items-end gap-2">
                            {isLastUser && pendingAttachments.length > 0 && (
                              <CannyAttachmentPreview items={pendingAttachments} />
                            )}
                            {files.length > 0 && (
                              <div className="flex flex-wrap justify-end gap-1.5">
                                {files.map((file, fileIndex) => (
                                  // eslint-disable-next-line @next/next/no-img-element
                                  <img
                                    key={`${message.id}-file-${fileIndex}`}
                                    src={file.url}
                                    alt=""
                                    className="h-28 w-28 rounded-xl object-cover"
                                  />
                                ))}
                              </div>
                            )}
                            {text && (
                              <div className="liquid-glass whitespace-pre-wrap rounded-3xl px-4 py-2.5 text-sm text-white/90">
                                {text}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="max-w-[85%]">
                            {text && <CannyMarkdown>{text}</CannyMarkdown>}
                            {message.parts
                              .filter((part) => typeof part.type === 'string' && part.type.startsWith('tool-'))
                              .map((part, index) => (
                                <CannyToolPart
                                  key={`${message.id}-tool-${index}`}
                                  part={part as never}
                                  referenceUrl={referenceUrl ?? attachedReference}
                                  onReferenceUploaded={(url) => setReferenceUrl(url || null)}
                                  onChoice={(label) => void send(label)}
                                  onGenerationStarted={(jobs) =>
                                    void rememberGeneration(message.id, index, jobs)
                                  }
                                />
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {busy && <div className="text-xs text-white/35">{t('canny.typing')}</div>}
                </div>
              </div>
              <div className="flex flex-col items-center px-4 pb-6">
                {limitMessage ? (
                  <div className="liquid-glass w-full max-w-2xl rounded-3xl p-5 text-center">
                    <p className="mb-4 text-sm leading-relaxed text-white/70">{limitMessage}</p>
                    <Link
                      href="/login"
                      className="inline-flex h-10 items-center rounded-full bg-white px-5 text-sm font-medium text-[#09031a]"
                    >
                      {t('canny.auth.signup')}
                    </Link>
                  </div>
                ) : (
                <CannyComposer
                  value={input}
                  onChange={setInput}
                  onSubmit={() => void send(input)}
                  busy={busy}
                  onAttach={isAuthenticated ? attach : undefined}
                  mentionItems={isAuthenticated ? mentionItems : undefined}
                  onMention={addMention}
                />
                )}
                {images.length > 0 && (
                  <div className="mt-3">
                    <CannyAttachmentPreview
                      items={images.map((image) => ({ id: image.id, name: image.name, thumbUrl: image.url }))}
                      onOpen={(item) => removeImage(item.id)}
                    />
                  </div>
                )}
              <CannyAttachments documents={documents} onRemove={(id) => void remove(id)} />
                {(uploadError || imageError) && (
                  <p className="mt-2 text-xs text-coral-100">{uploadError || imageError}</p>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center px-4 pb-24">
              <h1 className="mb-8 text-center text-3xl font-medium tracking-tight text-white/90 sm:text-[2.1rem]">
                Чем займёмся?
              </h1>
              <CannyComposer
                value={input}
                onChange={setInput}
                onSubmit={() => void send(input)}
                busy={busy}
                autoFocus
                onAttach={isAuthenticated ? attach : undefined}
                mentionItems={isAuthenticated ? mentionItems : undefined}
                onMention={addMention}
              />
              {images.length > 0 && (
                <div className="mt-3">
                  <CannyAttachmentPreview
                    items={images.map((image) => ({ id: image.id, name: image.name, thumbUrl: image.url }))}
                    onOpen={(item) => removeImage(item.id)}
                  />
                </div>
              )}
              <CannyAttachments documents={documents} onRemove={(id) => void remove(id)} />
              {(uploadError || imageError) && (
                <p className="mt-2 text-xs text-coral-100">{uploadError || imageError}</p>
              )}
              <CannyEmptyState onPick={(prompt) => void send(prompt)} />
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
